/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

@file:Suppress("DuplicatedCode", "ObjectPropertyName", "Detekt:TooManyFunctions", "ClassName")
@file:KlangScript.Library("sprudel")

package io.peekandpoke.klang.sprudel.lang

import io.peekandpoke.klang.audio_bridge.AdsrCurves
import io.peekandpoke.klang.script.annotations.KlangScript
import io.peekandpoke.klang.script.ast.CallInfo
import io.peekandpoke.klang.sprudel.SprudelPattern
import io.peekandpoke.klang.sprudel._applyControlFromParams
import io.peekandpoke.klang.sprudel._liftOrReinterpretNumericalField
import io.peekandpoke.klang.sprudel._mapNumericField
import io.peekandpoke.klang.sprudel.lang.SprudelDslArg.Companion.asSprudelDslArgs

// -- notch -----------------------------------------------------------------------------------------------------------

private val notchFreqMutation = voiceSetter {
    val str = it?.toString() ?: return@voiceSetter
    notchf = str.toDoubleOrNull()
}

private fun applyNotchFreq(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.notchf }, update = notchFreqMutation)
    }

    return source._liftOrReinterpretNumericalField(args, notchFreqMutation)
}

private val notchQMutation = voiceSetter { nresonance = it?.asDoubleOrNull() }
private fun applyNotchQ(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.nresonance }, update = notchQMutation)
    }

    return source._liftOrReinterpretNumericalField(args, notchQMutation)
}

private val notchEnvMutation = voiceSetter { nfenv = it?.asDoubleOrNull() }
private fun applyNotchEnv(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.nfenv }, update = notchEnvMutation)
    }

    return source._liftOrReinterpretNumericalField(args, notchEnvMutation)
}

private val notchAttackMutation = voiceSetter { nfattack = it?.asDoubleOrNull() ?: nfattack }
private fun applyNotchAttack(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.nfattack }, update = notchAttackMutation)
    }

    return source._liftOrReinterpretNumericalField(args, notchAttackMutation)
}

private val notchDecayMutation = voiceSetter { nfdecay = it?.asDoubleOrNull() ?: nfdecay }
private fun applyNotchDecay(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.nfdecay }, update = notchDecayMutation)
    }

    return source._liftOrReinterpretNumericalField(args, notchDecayMutation)
}

private val notchSustainMutation = voiceSetter { nfsustain = it?.asDoubleOrNull() ?: nfsustain }
private fun applyNotchSustain(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.nfsustain }, update = notchSustainMutation)
    }

    return source._liftOrReinterpretNumericalField(args, notchSustainMutation)
}

private val notchReleaseMutation = voiceSetter { nfrelease = it?.asDoubleOrNull() ?: nfrelease }
private fun applyNotchRelease(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    args.singleMapperOrNull()?.let { mapper ->
        return source._mapNumericField(mapper, read = { it.nfrelease }, update = notchReleaseMutation)
    }

    return source._liftOrReinterpretNumericalField(args, notchReleaseMutation)
}

/**
 * The notch filter: centre, resonance and the centre envelope.
 *
 * A narrow band around the centre is cut and everything else passes, the opposite of a bandpass.
 * Higher `q` narrows the cut. Every note carries its own centre, width and envelope,
 * per voice.
 *
 * The envelope sweeps `freq` up by `env` semitones: attack is the time to reach full depth, decay
 * the fall to the sustain share, release the fall back to `freq` after the note ends. `env = 12`
 * doubles the centre, a negative `env` sweeps down, and without `env` the filter rests at `freq`.
 *
 * Every slot is independent and patternable; an omitted slot keeps its value, a named slot takes
 * a mapper (`notch(q = mul(2))`), and every slot reads back as a child: `notch.freq`, `notch.q`, `notch.env`, `notch.attack`, `notch.decay`, `notch.sustain`, `notch.release`.
 * With no argument at all, the pattern's own values are reinterpreted as `freq`.
 *
 * ```KlangScript(Playable)
 * note("c3 e3").s("saw").notch(1000, 8)                                       // a narrow cut at 1 kHz
 * ```
 *
 * ```KlangScript(Playable)
 * note("c3*4").s("saw").notch(freq = 500, q = 8, env = 24, attack = 0.01, decay = 0.4, sustain = 0.2)   // the notch sweeps two octaves on every hit
 * ```
 *
 * ```KlangScript(Playable)
 * note("c3 e3").s("saw").notch("800 1600").bpf(notch.freq.mul(2), 3)           // a band an octave above the notch
 * ```
 *
 * @param freq Centre frequency in Hz.
 * @param q Resonance, higher narrows the notch.
 * @param env Envelope depth in semitones.
 * @param attack Envelope attack in seconds.
 * @param decay Envelope decay in seconds.
 * @param sustain Envelope sustain, 0 to 1.
 * @param release Envelope release in seconds.
 * @tool SprudelNotchFilterEditor
 * @tool freq: SprudelNotchFilterEditor, SprudelNotchFilterSequenceEditor
 * @tool q: SprudelNResonanceEditor, SprudelNResonanceSequenceEditor
 * @tool env: SprudelNfEnvEditor, SprudelNfEnvSequenceEditor
 * @tool attack: SprudelNfAttackEditor, SprudelNfAttackSequenceEditor
 * @tool decay: SprudelNfDecayEditor, SprudelNfDecaySequenceEditor
 * @tool sustain: SprudelNfSustainEditor, SprudelNfSustainSequenceEditor
 * @tool release: SprudelNfReleaseEditor, SprudelNfReleaseSequenceEditor
 *
 * @scope voice
 * @category effects
 * @tags notch, freq, q, env, attack, decay, sustain, release, notch.freq, notch filter, filter, envelope
 */
@KlangScript.Function
fun SprudelPattern.notch(
    freq: PatternLike? = null,
    q: PatternLike? = null,
    env: PatternLike? = null,
    attack: PatternLike? = null,
    decay: PatternLike? = null,
    sustain: PatternLike? = null,
    release: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern {
    // A tail-only call must not touch freq: reinterpret runs only on a fully bare call.
    var p = if (freq != null || !(q != null || env != null || attack != null || decay != null || sustain != null || release != null)) {
        applyNotchFreq(this, listOfNotNull(freq).asSprudelDslArgs(callInfo))
    } else {
        this
    }
    if (q != null) p = applyNotchQ(p, listOf<Any?>(q).asSprudelDslArgs(callInfo?.forParam(1)))
    if (env != null) p = applyNotchEnv(p, listOf<Any?>(env).asSprudelDslArgs(callInfo?.forParam(2)))
    if (attack != null) p = applyNotchAttack(p, listOf<Any?>(attack).asSprudelDslArgs(callInfo?.forParam(3)))
    if (decay != null) p = applyNotchDecay(p, listOf<Any?>(decay).asSprudelDslArgs(callInfo?.forParam(4)))
    if (sustain != null) p = applyNotchSustain(p, listOf<Any?>(sustain).asSprudelDslArgs(callInfo?.forParam(5)))
    if (release != null) p = applyNotchRelease(p, listOf<Any?>(release).asSprudelDslArgs(callInfo?.forParam(6)))
    return p
}

/** Parses this string as a pattern, then applies [notch]. */
@KlangScript.Function
fun String.notch(
    freq: PatternLike? = null,
    q: PatternLike? = null,
    env: PatternLike? = null,
    attack: PatternLike? = null,
    decay: PatternLike? = null,
    sustain: PatternLike? = null,
    release: PatternLike? = null,
    callInfo: CallInfo? = null
): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).notch(freq, q, env, attack, decay, sustain, release, callInfo)

/** Chains a [notch] step onto this [PatternMapperFn]. */
@KlangScript.Function
fun PatternMapperFn.notch(
    freq: PatternLike? = null,
    q: PatternLike? = null,
    env: PatternLike? = null,
    attack: PatternLike? = null,
    decay: PatternLike? = null,
    sustain: PatternLike? = null,
    release: PatternLike? = null,
    callInfo: CallInfo? = null
): PatternMapperFn =
    this.chain { p -> p.notch(freq, q, env, attack, decay, sustain, release, callInfo) }

/**
 * The `notch` object: `notch(...)` sets the slots, and each slot reads back as a child,
 * `notch.freq`, `notch.q`, `notch.env`, `notch.attack`, `notch.decay`, `notch.sustain`, `notch.release`.
 *
 * @scope voice
 * @category effects
 * @tags notch, accessor
 */
@KlangScript.Library("sprudel")
@KlangScript.Object("notch")
object notch {

    /** The freq slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val freq: FieldAccessor = FieldAccessor { it.notchf }

    /** The q slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val q: FieldAccessor = FieldAccessor { it.nresonance }

    /** The env slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val env: FieldAccessor = FieldAccessor { it.nfenv }

    /** The attack slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val attack: FieldAccessor = FieldAccessor { it.nfattack }

    /** The decay slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val decay: FieldAccessor = FieldAccessor { it.nfdecay }

    /** The sustain slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val sustain: FieldAccessor = FieldAccessor { it.nfsustain }

    /** The release slot of each event, as a value other setters can read. */
    @KlangScript.Property
    val release: FieldAccessor = FieldAccessor { it.nfrelease }

    /**
     * The setter, see [SprudelPattern.notch].
     *
     * @param freq Centre frequency in Hz.
     * @param q Resonance, higher narrows the notch.
     * @param env Envelope depth in semitones.
     * @param attack Envelope attack in seconds.
     * @param decay Envelope decay in seconds.
     * @param sustain Envelope sustain, 0 to 1.
     * @param release Envelope release in seconds.
     */
    @KlangScript.Invoke
    operator fun invoke(
        freq: PatternLike? = null,
        q: PatternLike? = null,
        env: PatternLike? = null,
        attack: PatternLike? = null,
        decay: PatternLike? = null,
        sustain: PatternLike? = null,
        release: PatternLike? = null,
        callInfo: CallInfo? = null
    ): PatternMapperFn =
        { p -> p.notch(freq, q, env, attack, decay, sustain, release, callInfo) }
}

// -- notchCurves ----------------------------------------------------------------------------------------------------------

// The curve names have ONE home, `AdsrCurves` in audio_bridge; the rule is `adsrCurves`' (and `penvCurves`'):
// an unknown name, like an omitted stage, keeps the stage's current curve.
private val notchAttackCurveMutation = voiceSetter {
    nfAttackCurve = AdsrCurves.curveOf(it?.toString()) ?: nfAttackCurve
}

private val notchDecayCurveMutation = voiceSetter {
    nfDecayCurve = AdsrCurves.curveOf(it?.toString()) ?: nfDecayCurve
}

private val notchReleaseCurveMutation = voiceSetter {
    nfReleaseCurve = AdsrCurves.curveOf(it?.toString()) ?: nfReleaseCurve
}

private fun applyNfAttackCurve(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    return source._applyControlFromParams(args, notchAttackCurveMutation) { src, ctrl ->
        src.nfAttackCurve = ctrl.nfAttackCurve ?: src.nfAttackCurve
        src
    }
}

private fun applyNfDecayCurve(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    return source._applyControlFromParams(args, notchDecayCurveMutation) { src, ctrl ->
        src.nfDecayCurve = ctrl.nfDecayCurve ?: src.nfDecayCurve
        src
    }
}

private fun applyNfReleaseCurve(source: SprudelPattern, args: List<SprudelDslArg<Any?>>): SprudelPattern {
    return source._applyControlFromParams(args, notchReleaseCurveMutation) { src, ctrl ->
        src.nfReleaseCurve = ctrl.nfReleaseCurve ?: src.nfReleaseCurve
        src
    }
}

/**
 * Sets the shape of each stage of the notch filter's cutoff envelope ([notch]'s `env`, `attack`,
 * `decay`, `sustain`, `release`), the way [adsrCurves] shapes the amplitude envelope. Each stage is
 * independent; an omitted stage, or an unknown curve name, keeps its current curve, so
 * `notchCurves(decay = "linear")` changes only the decay.
 *
 * The curves are the same six as `adsrCurves`: `linear`, `square`, `cube`, `scurve`, `invsquare`,
 * `exponential`, with the same aliases. Unset, every stage is `exponential`, the default of every
 * modulation envelope on every surface (decision D3). A curve alone switches nothing on: without an
 * envelope on the `notch` there is no sweep to shape. The Ignitor filters name the same curves inside
 * their envelope: `x => x.adsr(a, d, s, r, e => e.curves(...))`.
 *
 * ```KlangScript(Playable)
 * note("c3*4").s("saw").notch(freq = 800, q = 2, env = 24, decay = 0.3, sustain = 0).notchCurves(decay = "cube")   // a fast first drop, a long tail
 * ```
 *
 * @param attack Curve name for the attack stage.
 * @param decay Curve name for the decay stage.
 * @param release Curve name for the release stage.
 *
 * @scope voice
 * @category effects
 * @tags notch, curve, envelope, shape, filter envelope
 */
@KlangScript.Function
fun SprudelPattern.notchCurves(
    attack: PatternLike? = null,
    decay: PatternLike? = null,
    release: PatternLike? = null,
    callInfo: CallInfo? = null,
): SprudelPattern {
    var p = this
    if (attack != null) p = applyNfAttackCurve(p, listOf<Any?>(attack).asSprudelDslArgs(callInfo?.forParam(0)))
    if (decay != null) p = applyNfDecayCurve(p, listOf<Any?>(decay).asSprudelDslArgs(callInfo?.forParam(1)))
    if (release != null) p = applyNfReleaseCurve(p, listOf<Any?>(release).asSprudelDslArgs(callInfo?.forParam(2)))
    return p
}

/**
 * Parses this string as a pattern and sets the notch filter envelope's stage curves (see [notchCurves]).
 *
 * @param attack Curve name for the attack stage.
 * @param decay Curve name for the decay stage.
 * @param release Curve name for the release stage.
 */
@KlangScript.Function
fun String.notchCurves(
    attack: PatternLike? = null,
    decay: PatternLike? = null,
    release: PatternLike? = null,
    callInfo: CallInfo? = null,
): SprudelPattern =
    this.toVoiceValuePattern(callInfo?.receiverLocation).notchCurves(attack, decay, release, callInfo)

/**
 * The `notchCurves` object: `notchCurves(attack, decay, release)` sets the notch filter envelope's stage curves
 * by name. The slots are names, not numbers, so the object carries the setter only and no readers
 * (the `adsrCurves` rule).
 *
 * ```KlangScript(Playable)
 * note("c3*4").s("saw").notch(freq = 800, q = 2, env = 24, decay = 0.3, sustain = 0).apply(notchCurves(decay = "linear"))
 * ```
 *
 * @scope voice
 * @category effects
 * @tags notch, curve, envelope, shape, filter envelope
 */
@KlangScript.Library("sprudel")
@KlangScript.Object("notchCurves")
object notchCurves {

    /**
     * The setter, see [SprudelPattern.notchCurves].
     *
     * @param attack Curve name for the attack stage.
     * @param decay Curve name for the decay stage.
     * @param release Curve name for the release stage.
     */
    @KlangScript.Invoke
    operator fun invoke(
        attack: PatternLike? = null,
        decay: PatternLike? = null,
        release: PatternLike? = null,
        callInfo: CallInfo? = null,
    ): PatternMapperFn = { p -> p.notchCurves(attack, decay, release, callInfo) }
}

/**
 * Creates a chained [PatternMapperFn] that sets the notch filter envelope's stage curves after the previous mapper.
 *
 * @param attack Curve name for the attack stage. Omit to keep the current curve.
 * @param decay Curve name for the decay stage. Omit to keep the current curve.
 * @param release Curve name for the release stage. Omit to keep the current curve.
 */
@KlangScript.Function
fun PatternMapperFn.notchCurves(
    attack: PatternLike? = null,
    decay: PatternLike? = null,
    release: PatternLike? = null,
    callInfo: CallInfo? = null,
): PatternMapperFn =
    this.chain { p -> p.notchCurves(attack, decay, release, callInfo) }
